import { Link } from 'react-router-dom';
import React, { useState, useEffect } from 'react';

const HelpCenterPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const quickLinks = [
    { title: 'Contact Support', description: 'Get help from our team', icon: '📧', path: '/contact' },
    { title: 'Report a Bug', description: 'Let us know about issues', icon: '🐛', path: '/contact' },
    { title: 'Feature Request', description: 'Suggest new features', icon: '💡', path: '/contact' },
    { title: 'Billing Support', description: 'Questions about payments', icon: '💳', path: '/contact' }
  ];

  const faqs = [
    {
      id: 1,
      question: 'How quickly will I get my familiarity scores?',
      answer: 'Your familiarity scores are calculated instantly after completing your profile. Our algorithm analyzes %Non-US IMG data and available positions in real-time to provide immediate compatibility scores for all programs.',
      category: 'features'
    },
    {
      id: 2,
      question: 'What data is used to calculate familiarity scores?',
      answer: 'Our algorithm uses each program\'s %Non-US IMG acceptance rate, number of available positions, and your specific profile criteria. The score represents your compatibility based on historical data and program statistics.',
      category: 'features'
    },
    {
      id: 3,
      question: 'Can I update my profile information later?',
      answer: 'Yes, you can update your profile at any time. When you make changes to your USMLE scores, graduation timeline, USCE experience, or visa requirements, your familiarity scores will automatically refresh for all programs.',
      category: 'account'
    },
    {
      id: 4,
      question: 'What do I get with premium features?',
      answer: 'Premium features include access to Step 2 CK cutoffs for all programs, complete familiarity scoring, unlimited access to all 600+ programs, and advanced filtering options. Pricing varies by country for accessibility.',
      category: 'billing'
    },
    {
      id: 5,
      question: 'How do I filter programs by visa sponsorship?',
      answer: 'Use the smart filters in your program list to select either J1 or H1B visa sponsorship. This ensures you only see programs that can actually sponsor your specific visa type, saving time in your application process.',
      category: 'features'
    },
    {
      id: 6,
      question: 'Why aren\'t my familiarity scores updating?',
      answer: 'If your scores aren\'t updating after profile changes, try refreshing the page. If the issue persists, clear your browser cache or contact support at info@revisableapp.com. Scores should update immediately after any profile modifications.',
      category: 'technical'
    },
    {
      id: 7,
      question: 'How does country-specific pricing work?',
      answer: 'We offer pricing in local currencies to make premium features accessible worldwide. Pricing is adjusted based on your country\'s purchasing power while maintaining the same feature set globally.',
      category: 'billing'
    },
    {
      id: 8,
      question: 'Can I access Step 2 CK cutoffs for all specialties?',
      answer: 'Yes, premium access includes Step 2 CK cutoff data for all specialties and programs in our database. This helps you make strategic decisions about which programs to target based on your actual scores.',
      category: 'features'
    },
{
  id: 9,
  question: 'What is your refund policy?',
  answer: 'We have a strict no-refund policy. All sales are final once you purchase access to our residency program list and premium features. Due to the instant digital delivery of proprietary data, absolutely no refunds are available once payment is processed. Please read our full refund policy before purchasing.',
  category: 'billing'
},
{
  id: 10,
  question: 'Why don\'t you offer refunds?',
  answer: 'Due to the instant digital delivery of valuable, proprietary medical residency data including familiarity scores and Step 2 CK cutoffs, all sales are final. Once you access this information, it cannot be "returned" as the knowledge has been permanently transferred to you.',
  category: 'billing'
},
{
  id: 11,
  question: 'Can I cancel my subscription after purchase?',
  answer: 'No, all purchases are final and cannot be cancelled once payment is processed. Due to the immediate access provided to our comprehensive residency program database, we do not allow cancellations after purchase. Please read our cancellation policy for full details.',
  category: 'billing'
},
    {
      id: 12,
      question: 'How quickly do you respond to support tickets?',
      answer: 'We respond to most support tickets within 4-6 hours during business hours (Monday-Friday, 9 AM - 6 PM EST). Urgent issues related to billing or technical problems are prioritized and typically resolved within 2-3 hours. Reach out to us at info@revisableapp.com',
      category: 'support'
    },
    {
      id: 13,
      question: 'What happens to my data if I cancel?',
      answer: 'Your profile data and saved programs remain accessible for 30 days after cancellation. During this period, you can reactivate your subscription to regain full access. After 30 days, premium features are disabled but your basic profile is preserved.',
      category: 'account'
    },
    {
      id: 14,
      question: 'Do you offer student discounts?',
      answer: 'Yes, we offer a 25% student discount for verified medical students. Contact support at info@revisableapp.com with proof of enrollment (student ID or enrollment letter) to apply the discount to your account. The discount applies to all subscription plans.',
      category: 'billing'
    }
  ];

  const filteredFaqs = faqs.filter(faq => 
    searchQuery === '' || 
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Support response times info
  const supportTimes = [
    { type: 'General Inquiries', time: '4-6 hours', icon: '💬' },
    { type: 'Technical Issues', time: '2-3 hours', icon: '🔧' },
    { type: 'Billing Questions', time: '2-3 hours', icon: '💳' },
    { type: 'Policy Questions', time: '2-3 hours', icon: '📋' }   
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Quick Support Button - Fixed Position */}
      <div className="fixed bottom-6 right-6 z-50">
        <div className="bg-white rounded-full shadow-lg border border-gray-200 p-1">
          <div className="flex space-x-2">
            <a
              href="mailto:info@revisableapp.com"
              className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-xl group"
              title="Email Support"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </a>
            <a
              href="https://wa.me/919650500689"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-600 text-white p-4 rounded-full transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-xl group"
              title="WhatsApp Support"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.512z"/>
              </svg>
            </a>
          </div>
        </div>
        
        {/* Support tooltip */}
        <div className="absolute bottom-full right-0 mb-2 opacity-0 hover:opacity-100 transition-opacity duration-200">
          <div className="bg-gray-800 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap">
            Quick Support: Email or WhatsApp
            <div className="absolute top-full right-4 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="/images/logo.png" 
                alt="Residency Companion Logo" 
                className="w-10 h-10 mr-4 object-contain"
              />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                Residency Companion
              </h1>
            </Link>
            <div className="flex items-center space-x-1">
              <Link 
                to="/login"
                className="text-gray-600 hover:text-gray-900 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:bg-gray-50"
              >
                Sign In
              </Link>
              <Link 
                to="/signup"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-teal-50">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob-enhanced"></div>
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-teal-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob-enhanced animation-delay-2000"></div>
        </div>
        
        <div className={`relative z-10 text-center px-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="max-w-4xl mx-auto">
            <div className="mb-6 inline-flex items-center space-x-2 bg-white/70 backdrop-blur-sm border border-gray-200 rounded-full px-4 py-2 shadow-sm">
              <span className="w-4 h-4 text-blue-600">🎓</span>
              <span className="text-sm text-gray-700 font-medium">IMG Residency Matching Support</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-thin text-gray-900 mb-6 leading-tight">
              Help
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent font-light">
                Center
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto font-light leading-relaxed">
              Find answers and guidance for making the most of your residency matching journey
            </p>

            {/* Quick Contact Info */}
            <div className="mb-8 inline-flex items-center space-x-6 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-full px-6 py-3 shadow-sm">
              <a 
                href="mailto:info@revisableapp.com"
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium">info@revisableapp.com</span>
              </a>
              <div className="h-4 w-px bg-gray-300"></div>
              <a 
                href="https://wa.me/919650500689"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-green-600 hover:text-green-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.512z"/>
                </svg>
                <span className="text-sm font-medium">+91 96505 00689</span>
              </a>
            </div>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="text-gray-400 text-xl">🔍</span>
                </div>
                <input
                  type="text"
                  placeholder="Search for frequently asked questions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-12 pr-4 py-4 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/80 backdrop-blur-sm text-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-thin text-gray-900 mb-4">Quick Actions</h2>
            <p className="text-lg text-gray-600">Common tasks and support options</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {quickLinks.map((link, index) => (
              <Link
                key={index}
                to={link.path}
                className={`bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 text-center cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-4xl mb-4">{link.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{link.title}</h3>
                <p className="text-gray-600 text-sm">{link.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Support Response Times */}
      <section className="py-16 bg-gradient-to-r from-blue-50 to-teal-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-thin text-gray-900 mb-4">Support Response Times</h2>
            <p className="text-lg text-gray-600">We're committed to helping you quickly during business hours</p>
            <p className="text-sm text-gray-500 mt-2">Monday - Friday, 9 AM - 6 PM EST</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {supportTimes.map((item, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl p-6 text-center shadow-sm border border-gray-200 transition-all duration-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-3xl mb-3">{item.icon}</div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.type}</h3>
                <p className="text-2xl font-bold text-blue-600 mb-1">{item.time}</p>
                <p className="text-xs text-gray-500">average response</p>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <div className="inline-flex items-center space-x-2 bg-white/70 backdrop-blur-sm border border-gray-200 rounded-full px-4 py-2 shadow-sm">
              <span className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></span>
              <span className="text-sm text-gray-700">Support team is currently online</span>
            </div>
          </div>
        </div>
      </section>
      {/* Billing Information */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-3xl p-8 border border-gray-200">
            <div className="text-center mb-8">
              <div className="text-4xl mb-4">💳</div>
              <h2 className="text-3xl font-thin text-gray-900 mb-4">Billing Information</h2>
              <p className="text-lg text-gray-600">Payment and billing details</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-center max-w-3xl mx-auto">
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Business Name</h3>
                <p className="text-gray-600">REVIO EDUCATION PRIVATE LIMITED</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Billing Label</h3>
                <p className="text-gray-600">Charges appear as "Revisable AI" on your bank statement</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
{/* No Refund Policy Highlight */}
<section className="py-16 bg-white">
  <div className="max-w-4xl mx-auto px-6 lg:px-8">
    <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-3xl p-8 border-2 border-red-200">
      <div className="text-center mb-8">
        <div className="text-4xl mb-4">🚫</div>
        <h2 className="text-3xl font-thin text-gray-900 mb-4">No Refunds Policy</h2>
        <p className="text-lg text-gray-600">All sales are final - no refunds or cancellations</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-center max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-red-200">
          <div className="text-2xl font-bold text-red-600 mb-2">NO</div>
          <p className="text-gray-600 text-sm">Refunds after purchase</p>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-red-200">
          <div className="text-2xl font-bold text-red-600 mb-2">NO</div>
          <p className="text-gray-600 text-sm">Cancellations after purchase</p>
        </div>
      </div>
      
      <div className="mt-6 text-center">
        <Link
          to="/refund-policy"
          className="text-red-600 hover:text-red-700 font-medium underline"
        >
          Read Full Refund Policy →
        </Link>
      </div>
    </div>
  </div>
</section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-thin text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Quick answers to common questions about IMG residency matching</p>
          </div>

          <div className="space-y-4">
            {filteredFaqs.map((faq, index) => (
              <div
                key={faq.id}
                className={`bg-white border border-gray-200 rounded-2xl overflow-hidden transition-all duration-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <span className={`inline-block px-2 py-1 text-xs rounded-full mr-3 ${
                      faq.category === 'billing' ? 'bg-green-100 text-green-700' :
                      faq.category === 'features' ? 'bg-blue-100 text-blue-700' :
                      faq.category === 'technical' ? 'bg-orange-100 text-orange-700' :
                      faq.category === 'support' ? 'bg-purple-100 text-purple-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {faq.category}
                    </span>
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                  </div>
                  <span className={`transform transition-transform duration-300 text-blue-600 ${
                    expandedFaq === faq.id ? 'rotate-180' : ''
                  }`}>
                    ↓
                  </span>
                </button>
                
                {expandedFaq === faq.id && (
                  <div className="px-6 pb-6">
                    <div className="border-t border-gray-100 pt-4">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredFaqs.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">❓</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No FAQs found</h3>
              <p className="text-gray-600">Try adjusting your search terms</p>
            </div>
          )}
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-teal-600">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-thin text-white mb-6">
            Still Need Help?
          </h2>
          <p className="text-xl text-blue-100 mb-8 font-light">
            Our support team is here to help you succeed in your residency matching journey
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link
              to="/contact"
              className="bg-white text-blue-600 px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Contact Support
            </Link>
            <Link
              to="/features"
              className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:bg-white hover:text-blue-600"
            >
              Explore Features
            </Link>
          </div>
          
          {/* Direct Contact Options */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="mailto:info@revisableapp.com"
              className="flex items-center space-x-2 text-blue-100 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>info@revisableapp.com</span>
            </a>
            <div className="h-4 w-px bg-blue-300 hidden sm:block"></div>
            <a
              href="https://wa.me/919650500689"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-blue-100 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.512z"/>
              </svg>
              <span>+91 96505 00689</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-6">
                <img 
                  src="/images/logo.png" 
                  alt="Residency Companion Logo" 
                  className="w-12 h-12 mr-4 object-contain"
                />
                <div>
                  <h4 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                    Residency Companion
                  </h4>
                  <p className="text-sm text-gray-500 font-medium">IMG Residency Matching Platform</p>
                </div>
              </div>
              <p className="text-gray-600 font-light">
                Your strategic partner in medical residency applications.
              </p>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-4">Product</h5>
              <ul className="space-y-3 text-gray-600">
                <li><Link to="/features" className="hover:text-blue-600 transition-colors">Features</Link></li>
                <li><Link to="/how-it-works" className="hover:text-blue-600 transition-colors">How It Works</Link></li>
                <li><Link to="/api" className="hover:text-blue-600 transition-colors">API</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-4">Support</h5>
              <ul className="space-y-3 text-gray-600">
                <li><Link to="/help" className="text-blue-600 font-medium">Help Center</Link></li>
                <li><Link to="/contact" className="hover:text-blue-600 transition-colors">Contact Us</Link></li>
                <li><Link to="/privacy" className="hover:text-blue-600 transition-colors">Privacy</Link></li>
                <li><Link to="/refund-policy" className="hover:text-blue-600 transition-colors">Refund Policy</Link></li>
                <li><Link to="/cancellation-policy" className="hover:text-blue-600 transition-colors">Cancellation Policy</Link></li>
                <li><Link to="/terms" className="hover:text-blue-600 transition-colors">Terms</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-4">Company</h5>
              <ul className="space-y-3 text-gray-600">
                <li><Link to="/about" className="hover:text-blue-600 transition-colors">About</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-12 pt-8 text-center">
            <p className="text-gray-500 text-sm">
              © 2025 Revio Education Pvt Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes blob-enhanced {
          0% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
          25% { transform: translate(20px, -30px) scale(1.05) rotate(5deg); }
          50% { transform: translate(-15px, 10px) scale(0.95) rotate(-3deg); }
          75% { transform: translate(25px, -20px) scale(1.08) rotate(7deg); }
          100% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
        }
        .animate-blob-enhanced { animation: blob-enhanced 9s ease-in-out infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default HelpCenterPage;